<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import CannedAttachmentsPreview from './CannedAttachmentsPreview.vue';
import { MAXIMUM_FILE_UPLOAD_SIZE } from 'shared/constants/messages';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

const props = defineProps({
  responseContent: { type: String, default: '' },
  onClose: { type: Function, default: () => {} },
});

const { t } = useI18n();
const store = useStore();

const show = ref(true);
const shortCode = ref('');
const content = ref(props.responseContent || '');
const attachments = ref([]);
const addCanned = ref({ showLoading: false });

const rules = {
  shortCode: { required, minLength: minLength(2) },
  content: { required },
};
const v$ = useVuelidate(rules, { shortCode, content });

function resetForm() {
  shortCode.value = '';
  content.value = '';
  attachments.value = [];
  v$.value.$reset();
}

function handleFileUpload(event) {
  const files = Array.from(event.target.files);
  files.forEach(file => {
    if (!checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
      useAlert(
        t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: MAXIMUM_FILE_UPLOAD_SIZE,
        })
      );
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        attachments.value.push({ resource: file, thumb: reader.result });
      };
    }
  });
  event.target.value = null;
}

function removeAttachment(updated) {
  attachments.value = updated;
}

async function addCannedResponse() {
  addCanned.value.showLoading = true;
  try {
    await store.dispatch('createCannedResponse', {
      short_code: shortCode.value,
      content: content.value,
      attachments: attachments.value.map(a => a.signed_id || a.resource),
    });
    useAlert(t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
    resetForm();
    props.onClose();
  } catch (error) {
    const message = error?.message || t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(message);
  } finally {
    addCanned.value.showLoading = false;
  }
}
</script>

<template>
  <Modal v-model:show="show" @close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="t('CANNED_MGMT.ADD.TITLE')"
        :header-content="t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="w-full mt-4">
          <input type="file" multiple class="mb-2" @change="handleFileUpload" />
          <CannedAttachmentsPreview
            v-if="attachments.length"
            :attachments="attachments"
            @remove-attachment="removeAttachment"
          />
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              addCanned.showLoading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}

:deep(.ProseMirror-woot-style p) {
  @apply text-base;
}
</style>
